import jwtDecode from 'jwt-decode';
import { BehaviorSubject, filter, map } from 'rxjs';

import { isNonNull } from '../utils/isNonNull.fn';
import moment from 'moment-timezone';

enum TokenNames {
  ADMIN_TOKEN = 'adminToken',
  PLATFORM_TOKEN = 'platformToken',
}

export enum UserRole {
  APTITUDE_USER = 'APTITUDE_USER',
  PROSPECTIVE_USER = 'PROSPECTIVE_USER',
  SELLER_MIN_MAX_PRICING_EDIT = 'SELLER_MIN_MAX_PRICING_EDIT',
  ROLE_APTITUDE_ADMIN = 'ROLE_APTITUDE_ADMIN',
  APTITUDE_ADMIN_EDIT = 'APTITUDE_ADMIN_EDIT',
}

export type IJwtPayload = IJwtPayloadV1 | IJwtPayloadV2AndAbove;

interface IJwtPayloadV2AndAbove {
  //short for Subject, usually the userIdcd leg
  sub?: string;
  // expiration in utc seconds
  exp: number;
  firstName: string;
  lastName: string;
  org: AptitudeOrganization;
  roles: UserRole[];
  vizientUserGuid?: string;
  version: string;
  email?: string;
  loginSource: 'okta' | 'password';
}

interface AptitudeOrganization {
  orgType: 'SELLER' | 'INTERNAL';
  name: string;
  aptitudeAgreementStatus: 'NONE' | 'ACTIVE' | 'PENDING' | 'TERMINATED';
  id: number;
  groupOrgIds: number[];
}

export interface IJwtPayloadV1 {
  sub: string;
  orgType: 'Group' | 'HCO' | 'Supplier';
  orgName: string;
  userName: string;
  userId: number;
  // The same as 'roles' in the v2 token but as a comma seperated
  // string each item prefixed with the word 'ROLE_"
  authorities: string;
  loginSource: 'Aptitude' | '127.0.0.1';
  orgId: number;
  exp: number;
}

export class UnauthenticatedTokenPayload {
  getUserName = () => 'anonymous';
}

export class JwtPayload {
  constructor(private rawPayload: IJwtPayload) {}

  hasOrganization() {
    return (
      this.isV1TokenTypeGuard(this.rawPayload) && this.rawPayload.orgId !== null
    );
  }

  timeToExpirationInSeconds() {
    const currentTimeInSeconds = moment.utc().valueOf() / 1000;
    return this.rawPayload.exp - currentTimeInSeconds;
  }

  isNonAptitudeUser() {
    return !this.isAptitudeUser() && this.rawPayload.loginSource === 'okta';
  }

  isAptitudeUser() {
    return (
      this.isV1TokenTypeGuard(this.rawPayload) ||
      this.hasRole(UserRole.APTITUDE_USER) ||
      this.rawPayload.loginSource !== 'okta'
    );
  }

  shouldBeSentToAdminOnSignIn() {
    return (
      this.isV2TokenOrAboveTypeGuard(this.rawPayload) &&
      this.rawPayload.org.orgType === 'INTERNAL'
    );
  }

  isAdminReadUser() {
    this.hasRole(UserRole.ROLE_APTITUDE_ADMIN);
  }

  isAdminEditUser() {
    this.hasRole(UserRole.APTITUDE_ADMIN_EDIT);
  }

  isProspectiveSeller() {
    return (
      this.isV2TokenOrAboveTypeGuard(this.rawPayload) &&
      this.rawPayload.org.orgType === 'SELLER' &&
      this.hasRole(UserRole.PROSPECTIVE_USER)
    );
  }

  isTokenExpired() {
    return this.timeToExpirationInSeconds() <= 0;
  }

  isv1token() {
    return !this.isv2tokenOrAbove();
  }

  isv2tokenOrAbove() {
    return this.isV2TokenOrAboveTypeGuard(this.rawPayload);
  }

  private isV1TokenTypeGuard(token: IJwtPayload): token is IJwtPayloadV1 {
    return !this.isV2TokenOrAboveTypeGuard(token);
  }

  private isV2TokenOrAboveTypeGuard(
    token: IJwtPayload,
  ): token is IJwtPayloadV2AndAbove {
    return !!(token as IJwtPayloadV2AndAbove).version;
  }

  isOktaToken() {
    return this.rawPayload.loginSource === 'okta';
  }

  hasRole(userRole: UserRole) {
    if (this.isV1TokenTypeGuard(this.rawPayload)) {
      return this.rawPayload.authorities.includes(userRole);
    }
    return this.rawPayload.roles.includes(userRole);
  }

  getUserName() {
    if (this.isV1TokenTypeGuard(this.rawPayload)) {
      return this.rawPayload.userName;
    }
    return this.rawPayload.email || this.rawPayload.sub || 'anonymous_username';
  }
}

export class TokenKeeper {
  private static platformSingleton: TokenKeeper;
  private static adminSingleton: TokenKeeper;
  private constructor(private tokenName: TokenNames) {}

  #token$ = new BehaviorSubject(this.tokenFromLocalStorage());
  token$ = this.#token$.pipe(filter(isNonNull), filter(this.isValidToken));
  tokenPayload$ = this.token$.pipe(
    map((token) => new JwtPayload(jwtDecode<IJwtPayload>(token))),
  );
  tokenPayloadNullable$ = this.#token$.pipe(
    map((tokenOrNull) =>
      tokenOrNull
        ? new JwtPayload(jwtDecode<IJwtPayload>(tokenOrNull))
        : new UnauthenticatedTokenPayload(),
    ),
  );

  // AngularJS and Angular agnostic
  static getPlatformSingleton(): TokenKeeper {
    if (!this.platformSingleton) {
      this.platformSingleton = new this(TokenNames.PLATFORM_TOKEN);
    }

    return this.platformSingleton;
  }

  static getAdminSingleton(): TokenKeeper {
    if (!this.adminSingleton) {
      this.adminSingleton = new this(TokenNames.ADMIN_TOKEN);
    }

    return this.adminSingleton;
  }

  static isAptitudeEndpoint(url: string) {
    try {
      const splitUrl = new URL(url);

      const aptitudeDomainsRegex =
        /(localhost|127.0.0.1|aptitudedev.com|aptitudemarketplace.com)$/gi;
      const found = splitUrl.hostname.match(aptitudeDomainsRegex);

      return found !== null;
    } catch (error) {
      // new URL(url) throws on relative urls like '/aptitude/api/'
      // but the only urls like this will be our own
      return true;
    }
  }

  setToken(newToken: string) {
    this.#token$.next(newToken);
    window.localStorage.setItem(this.tokenName, newToken);
  }

  clearToken() {
    this.#token$.next(null);
    window.localStorage.removeItem(this.tokenName);
  }

  getToken() {
    return this.tokenFromLocalStorage();
  }

  getTokenFromLocalStorage() {
    const token = this.tokenFromLocalStorage();
    if (!token) throw new Error('There is no token');
    return token;
  }

  getPayloadFromLocalStorage() {
    const token = this.getTokenFromLocalStorage();
    return new JwtPayload(jwtDecode<IJwtPayload>(token));
  }

  private tokenFromLocalStorage() {
    return window.localStorage.getItem(this.tokenName);
  }

  private isValidToken(token: string) {
    const matchTokenSeparators = /\./g;

    // Make sure this looks like a legit JWT before loading it.
    return (
      typeof token === 'string' &&
      (token.match(matchTokenSeparators) || []).length === 2
    );
  }
}
